import { formatDate, pl, t } from "@hakili/i18n";
import {
  Avatar,
  Button,
  Card,
  CHIFFRE,
  Chip,
  Field,
  GuardedButton,
  METADONNEE,
  Modal,
  Select,
  SidePanel,
  TEXTE_SECONDAIRE,
  TEXTE_TERTIAIRE,
  type Tone,
} from "@hakili/ui";
import { Check, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import type {
  Deactivation,
  DoorResult,
  Inventory,
  InventoryVehicle,
  TeamMember,
} from "../session/useSession.js";
import { stageLabel, stageTone, weighsOnTheTeam } from "./stages.js";
import { META_TEXT } from "./vehicle-row.js";

/**
 * `P0-DLW-006`, `00-SPINE` 6.7: **« Équipe » , the dealership runs its own
 * team.**
 *
 * ## The shape is borrowed, and the borrowing is recorded
 *
 * `docs/mockups/hakili-dealer.jsx` had no team screen at all when this screen
 * was built , the copy that was true of is kept at
 * `docs/mockups/hakili-dealer-2026-08-20.jsx`. **The 6 September mockup draws
 * one** (`:3243`), and names this entry's own obligation as what it discharges
 * (`:2976`). The attribution below stays true: `EquipeScreen` in
 * `hakili-console.jsx:9894` is the shape this product already uses for exactly
 * this gesture: a list carrying role and state, a greyed and dated row for a
 * closed account, an invitation dialog, and a removal that refuses while open
 * work remains. It is borrowed at design fidelity **through `@hakili/ui`**, so
 * the dealership's team screen is the console's screen rather than a second
 * design of one idea. `OD-9` records the borrowing and the dealer mockup
 * addendum that is owed.
 *
 * **What is deliberately NOT borrowed** is the console's charge-and-availability
 * block, its four statistics and its portfolio transfer. Those measure a chargé
 * de dossier's workload, which a dealership's employee cannot carry at all
 * (`P0-IDN-069`, and the matrix behind it). Rendering them here would be four
 * tiles reading zero and a control that acts on nothing.
 *
 * ## « Réattribuer et retirer », and what it is today
 *
 * 6.7 books offboarding-by-reassignment as law and names the bulk reassignment
 * door as OWED , the round after Round D. What the law requires of this round
 * is that the control carry the gesture's shape from today, *"so that the day
 * the door exists the control does not have to be redesigned, and so that
 * nobody learns the wrong habit meanwhile."*
 *
 * So the dialog reads the count first, from `GET /api/v1/users/{id}/deactivation`
 * , `A50`, because a screen that counted the files itself would be the second
 * implementation of a guard `identity` owns. At zero it says in words what a
 * removal does and offers the confirm; above zero it shows the target the
 * reassignment will default to and keeps the confirm disabled, carrying the
 * door's own reading of why.
 *
 * ## Every sentence a refusal makes is the server's
 *
 * The api answers *« Sortie impossible : 3 dossiers ouverts doivent d'abord être
 * transférés. »*, which tells a manager what to do. `R5` of 9.2.2 keeps that
 * sentence stored with the guard, and this screen prints it as written rather
 * than composing a second phrasing of one rule.
 */

/** What this screen needs of a session, and no more. */
/** The stages the drawing counts as « visites en cours » (`hakili-dealer.jsx:3257`). */
const VISITING: ReadonlySet<string> = new Set(["demande", "visite"]);

/**
 * `round F6`, `OD-15` and `OD-16`: **one person's load, DERIVED from the park.**
 *
 * The drawing's own `chargeDe` (`hakili-dealer.jsx:920-932`) and its own rule:
 * *trois comptes dérivés, zéro constante écrite à la main*. Nothing here is
 * stored and nothing is fetched per person , the screen already reads the whole
 * park for its four tiles, and this is that same list, grouped.
 *
 * `tenus` is everything in their name; `enCharge` is what still weighs on the
 * dealership , the drawing's own words: *une voiture retirée du catalogue ou
 * déjà remise à son acheteur ne lui demande plus rien*.
 */
export interface PersonLoad {
  readonly held: readonly InventoryVehicle[];
  readonly weighing: number;
  readonly visits: number;
}

/**
 * `round F6`, 6.7: **who may take a leaving person's cars.**
 *
 * Everybody active except the person leaving , the drawing's own `candidats`
 * (`hakili-dealer.jsx:3126`) , and the MANAGER first, because he is the law's
 * default and a select puts its default at the top.
 */
export function candidates(
  members: readonly TeamMember[],
  leavingId: string,
  managerId: string,
): readonly TeamMember[] {
  const eligible = members.filter((row) => row.status === "actif" && row.id !== leavingId);
  return [...eligible].sort((a, b) => {
    if (a.id === managerId) return -1;
    if (b.id === managerId) return 1;
    return a.fullName.localeCompare(b.fullName, "fr-FR");
  });
}

/** A person's name, or the empty string , never an id shown to a reader. */
export function nameOf(members: readonly TeamMember[], id: string): string {
  return members.find((row) => row.id === id)?.fullName ?? "";
}

/**
 * `FX-2a`, `M3` (`F7`): **a person offered in a picker, said in a way that
 * identifies them.**
 *
 * `TR-1` named the hole by line: the options were built from `candidates(…)` and
 * labelled with `fullName` alone, so *« two people both named Abdoulaye Koita,
 * both AK »* were the SAME option twice, and the founder's own team is where that
 * case comes from.
 *
 * **The email is what this screen already identifies people by.** Every row of
 * the list carries it under the name (`team-email-*`), so the picker is not
 * inventing a convention , it is keeping the one the list beside it holds. The
 * drawing's own picker shows the name only (`hakili-dealer.jsx:3785`), and that
 * divergence is named in the round's addendum rather than taken silently: a
 * drawing made with one of each role cannot show the case two namesakes make.
 *
 * `selfId` carries 6.7's default marker, because the manager is the target a
 * removal falls back to and a reader should see which one that is.
 */
export function labelFor(person: TeamMember, selfId: string): string {
  const identified = `${person.fullName} (${person.email})`;
  return person.id === selfId ? `${identified} ${t("dealer.team.removeTargetDefault")}` : identified;
}

export function loadOf(park: readonly InventoryVehicle[], userId: string): PersonLoad {
  const held = park.filter((car) => car.assigneeUserId === userId);
  return {
    held,
    weighing: held.filter((car) => weighsOnTheTeam(car.stage)).length,
    visits: held.filter((car) => VISITING.has(car.stage)).length,
  };
}

export interface TeamProps {
  /** The caller's own id: which row is theirs, and 6.7's default reassignment target. */
  readonly selfId: string;
  /**
   * `FX-1`, `M1` (`F6`): **the caller's own right, as `/me` answered it.**
   *
   * The staging sitting of 20 to 22 September found an employee offered
   * « Réattribuer et retirer » on every row but her own. The gate was the ROW's
   * shape and never the CALLER's right, so the screen invited a gesture the
   * server refuses `403` , `TR-1` proved the refusal on all five
   * responsable-only doors. `/me` has carried `gererEquipeConcession` as `"non"`
   * since `P0-DLW-018` precisely so a front can tell a REFUSED right from an
   * absent one; this prop is that answer, read through `opensControl`.
   */
  readonly manages: boolean;
  /** From `/me`. Empty means this principal may invite nobody, and the control does not render. */
  readonly mayInviteRoles: readonly string[];
  readonly listTeam: () => Promise<DoorResult<readonly TeamMember[]>>;
  /**
   * `round F`, `F4`: the park, for two of the four figures the drawing puts
   * above the list , « véhicules suivis » and « visites en cours ».
   *
   * **The SAME door the inventory and the dashboard read**, so the three
   * screens cannot disagree about how many cars are in play. A count computed
   * from a second source is a second answer waiting to contradict the first.
   *
   * A refusal here empties the two tiles and leaves the team list alone: a
   * manager who cannot read the park can still run his team.
   */
  readonly listVehicles: () => Promise<DoorResult<Inventory>>;
  readonly invite: (input: {
    fullName: string;
    email: string;
    role: string;
  }) => Promise<DoorResult<{ delivered: boolean }>>;
  readonly readDeactivation: (userId: string) => Promise<DoorResult<Deactivation>>;
  /**
   * `round F6`, 6.7: the removal takes WHO TAKES THE CARS, when there are any.
   * The gesture does not split, so the target rides the same call that closes
   * the account.
   */
  readonly deactivate: (userId: string, reassignTo?: string) => Promise<DoorResult<void>>;
  /** `round F5`: « Renvoyer », for a row still waiting on its invitation. */
  readonly resendInvitation: (invitationId: string) => Promise<DoorResult<{ delivered: boolean }>>;
  /**
   * `FX-2a`, `M3` (`F7`): **one car out of one hand, without waiting for a
   * departure.**
   *
   * The door has existed since `round F6` (`POST /api/v1/me/vehicles/{id}/assignee`,
   * behind `gererEquipeConcession`) and Équipe offered no way to it: the only
   * gesture here was « Réattribuer et retirer », which is a DEPARTURE, and the
   * per-vehicle path was the car's own page. The drawing says otherwise over this
   * very list , *« Chaque ligne se réattribue une à une, sans attendre un
   * départ »* (`hakili-dealer.jsx:3728`) , and its Équipe block says what the
   * responsable comes here for: *« comment faire passer une voiture d'une main à
   * l'autre sans rien perdre »*.
   *
   * **Optional, and absent rather than refused**, exactly as the car's own page
   * takes it: `App` passes it only where the right opens, so a screen that cannot
   * do it does not draw it, and the door refuses anyone else in its own words
   * regardless of what this screen renders.
   */
  readonly assignVehicle?: (
    id: string,
    userId: string,
  ) => Promise<DoorResult<{ id: string; fullName: string }>>;
  /**
   * `round F5`, to the pair: the dealership's name, under the panel's title.
   *
   * The drawing writes it there (`hakili-dealer.jsx:3037`) and the reason is
   * the gesture: a manager is about to give somebody a key to ONE house, and
   * the panel says which. It comes from `/me` like every other fact about the
   * caller's own organisation; absent, the line is not drawn rather than
   * guessed.
   */
  readonly organisation?: string | null;
}

/**
 * The api's role identifiers, and the words a person reads.
 *
 * `DECISION ADDED 05-01`: an enum value is an identifier and never a word, so
 * the mapping lives here and the wire keeps `employeConcession`. An unknown
 * role renders as itself , a screen that printed a default label would be
 * telling a manager something the api never said.
 */
const ROLE_LABEL: Readonly<Record<string, string>> = {
  responsableConcession: "dealer.team.roleManager",
  employeConcession: "dealer.team.roleMember",
};

/**
 * The chip's shorter form, `round F`, `F4`.
 *
 * The drawing puts « Responsable » and « Employé » on the row's chip
 * (`hakili-dealer.jsx:306-307`) where the full label , « Responsable de
 * concession » , would say « concession » on a screen that has said nothing
 * else since it opened. Same role, same identifier, one word less.
 */
const ROLE_CHIP: Readonly<Record<string, string>> = {
  responsableConcession: "dealer.team.roleManagerCourt",
  employeConcession: "dealer.team.roleMemberCourt",
};

/**
 * The word for a role, or `null` when this build has not met it.
 *
 * `DECISION ADDED 05-01`: an enum value is an identifier and never a word. A
 * role the api renamed renders as ITSELF rather than as a default label, and
 * this is the one place that decision is written , three callers used to
 * re-derive it, each with its own index expression.
 */
const roleWord = (table: Readonly<Record<string, string>>, role: string): string | null => {
  const key = table[role];
  return key === undefined ? null : t(key);
};

/**
 * The order the drawing sorts people in (`hakili-dealer.jsx:3270-3275`), and
 * each of its three steps answers a question a manager actually asks.
 *
 * A closed account LAST , it is history, and history does not belong at the top
 * of a list of people you can call. Then seniority, because « who runs this
 * place » is the first thing a new employee looks for. Then the name, so the
 * order is stable and a row does not move under the cursor between renders.
 */
const SENIORITY: Readonly<Record<string, number>> = {
  responsableConcession: 0,
  employeConcession: 1,
};

function inDrawnOrder(members: readonly TeamMember[]): readonly TeamMember[] {
  return [...members].sort((left, right) => {
    const leftGone = left.status === "desactive";
    const rightGone = right.status === "desactive";
    if (leftGone !== rightGone) return leftGone ? 1 : -1;
    const rank = (SENIORITY[left.role] ?? 9) - (SENIORITY[right.role] ?? 9);
    if (rank !== 0) return rank;
    return left.fullName.localeCompare(right.fullName, "fr-FR");
  });
}

/**
 * The api's own status vocabulary, and the words a person reads for each.
 *
 * **A `Map` and not an object, and the reason is a rule rather than a taste.**
 * `actif`, `invitationEnAttente` and `desactive` are `identity.user_status`'s
 * values , the wire's words, frozen French. As bare object keys they are
 * identifiers this file DECLARES, and the founder's decision of 5 September is
 * that every new name is English; quoting them does not hold, because the
 * formatter strips quotes from keys that do not need them. A `Map` keyed by
 * strings says what is true: these are DATA the server sent, not names this
 * screen invented. Renaming them was never available , the map has to match the
 * wire.
 *
 * `ROLE_LABEL` above keeps its object form: its keys are frozen vocabulary that
 * predates the decision and are already on the list.
 */
const STATUS_LABEL = new Map<string, string>([
  ["actif", "dealer.team.statusActive"],
  ["invitationEnAttente", "dealer.team.statusInvited"],
  ["desactive", "dealer.team.statusRemoved"],
]);

/** A date that stays whole: the spaces inside « 18 juin 2026 » do not break (R's review of #929). */
const whole = (date: string): string => date.replaceAll(/[ \u202f]/gu, "\u00a0");

const STATUS_TONE = new Map<string, Tone>([
  ["actif", "ok"],
  ["invitationEnAttente", "attention"],
  ["desactive", "neutre"],
]);

/**
 * The door's date, read as the day it names , **whether it arrives as a day or
 * as an instant.**
 *
 * `deactivated_on` is a Postgres `date` and arrives as `2026-09-05`. The `Z` is
 * explicit rather than left to the engine: `new Date("2026-09-05")` is UTC
 * midnight by specification but `new Date("2026-09-05T00:00:00")` is LOCAL
 * midnight, and the two are different days for half the world. Naming the zone
 * is one character against a date that silently slips by one.
 *
 * **`round F5` widened it, and a crashed screen is what bought the widening.**
 * `created_at` and `last_login_at` are `timestamptz` read as `::text`, so they
 * arrive as `2026-09-08 09:12:44.123456+00` , a SPACE, not a `T`. Appending the
 * day's suffix to one of those makes an Invalid Date, and `Intl.DateTimeFormat`
 * THROWS on one rather than printing something odd. The pair of 8 September
 * caught it: « Équipe » rendered « Une erreur inattendue est survenue » against
 * the real api while every unit control stayed green on a fixture that answered
 * `null` for both. The fixture was the lie, and it is corrected in the same
 * commit.
 *
 * **The first ten characters, and not a parse.** Every form the door sends
 * starts with `YYYY-MM-DD`, and what this function is for is the DAY. Reading
 * the instant would put the dealership's own midnight in play for a line that
 * says « dans l'équipe depuis le … ».
 */
const dayOf = (value: string): Date => new Date(`${value.slice(0, 10)}T00:00:00Z`);

/** The dialog's own state: which member, and what the door said about them. */
interface Removal {
  readonly member: TeamMember;
  /** `null` while the door has not answered. The confirm does not render before it does. */
  readonly verdict: Deactivation | null;
  readonly refusal: string | null;
}

export function Team({
  selfId,
  manages,
  mayInviteRoles,
  listTeam,
  listVehicles,
  invite,
  resendInvitation,
  readDeactivation,
  deactivate,
  assignVehicle,
  organisation = null,
}: TeamProps): React.JSX.Element {
  const [members, setMembers] = useState<readonly TeamMember[] | null>(null);
  const [park, setPark] = useState<readonly InventoryVehicle[]>([]);
  /** `round F6`: whose cars are unfolded under their row, or nobody's. */
  const [unfolded, setUnfolded] = useState<string | null>(null);
  /**
   * `FX-2a`, `M3` (`F7`): which LINE is being passed to another hand, or none.
   *
   * The car's id and not a boolean: two cars unfolded under one person must not
   * open one picker between them, which is what a flag would do.
   */
  const [handing, setHanding] = useState<string | null>(null);
  /** The door's own sentence when a reassignment is refused, shown on the line it refused. */
  const [handingRefusal, setHandingRefusal] = useState<string | null>(null);
  /**
   * `round F6`, 6.7: **who takes the cars of the person being removed.**
   *
   * The manager BY DEFAULT , the law's own words, *to a colleague or to the
   * manager by default* , which is what the drawing's « (par défaut) » marks
   * (`hakili-dealer.jsx:3196`). Chosen here and sent with the removal, because
   * a default chosen in the api would be the application deciding where a
   * dealership's cars go.
   */
  const [handTo, setHandTo] = useState<string>("");
  const [readFailure, setReadFailure] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    const answer = await listTeam();
    if (answer.ok) {
      setMembers(answer.value);
      setReadFailure(null);
      return;
    }
    // A list that could not be read is not an empty team, and the two must not
    // look alike: an empty list is a fact about the dealership, a refusal is a
    // fact about the request.
    setMembers(null);
    setReadFailure(answer.message);
  }, [listTeam]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * The park, for two of the four figures above the list.
   *
   * **A refusal here is silent on purpose**, and it is the one place on this
   * screen where silence is right: the team list has its own refusal with the
   * door's own sentence, and a manager who cannot read the forecourt can still
   * run his team. Two tiles reading `0` while the list works would be the
   * failure , so the tiles read `0` only over an ANSWER, and the park starts
   * empty because that is what « we have not asked yet » looks like on a
   * counter that has no other state.
   */
  useEffect(() => {
    let alive = true;
    void listVehicles().then((answer) => {
      if (alive && answer.ok) setPark(answer.value.vehicles);
    });
    return () => {
      alive = false;
    };
  }, [listVehicles]);

  // ---------------------------------------------------------------- invite
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteFailure, setInviteFailure] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  // **The only role `may_invite_roles` allows.** I16 decides who may be invited,
  // by data; this screen reads the decision and never makes it. With an empty
  // array the control does not render at all, which is the honest rendering of
  // "this principal may invite nobody".
  const invitableRole = mayInviteRoles[0] ?? null;

  /**
   * `round F5`, the drawing's own three reasons (`hakili-dealer.jsx:3011-3019`).
   *
   * **It answers `null` until something has been typed.** A form that makes
   * the lesson before it has been touched is a form that scolds an empty page,
   * and the drawing says so in its own comment: *« le formulaire ne fait pas la
   * leçon avant d'avoir été touché »*.
   *
   * The third reason reads the list the door already answered. It is a
   * courtesy and not the guard: `identity` refuses a second invitation to a
   * seated address in its own words, and this only saves a manager the round
   * trip.
   */
  const started = inviteName.trim() !== "" || inviteEmail.trim() !== "";
  const emailLooksReal = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(inviteEmail.trim());
  const alreadySeated = (members ?? []).some(
    (row) => row.email.toLowerCase() === inviteEmail.trim().toLowerCase() && row.status !== "desactive",
  );
  const inviteRefusal = !started
    ? t("dealer.team.inviteReasonName")
    : inviteName.trim().split(/\s+/u).length < 2
      ? t("dealer.team.inviteReasonName")
      : !emailLooksReal
        ? t("dealer.team.inviteReasonEmail")
        : alreadySeated
          ? t("dealer.team.inviteReasonAlready")
          : null;

  const send = async (): Promise<void> => {
    if (sending || invitableRole === null) return;
    setInviteFailure(null);
    if (inviteName.trim() === "" || !inviteEmail.includes("@")) {
      setInviteFailure(t("dealer.team.inviteFieldsRequired"));
      return;
    }
    setSending(true);
    const answer = await invite({
      fullName: inviteName.trim(),
      email: inviteEmail.trim(),
      role: invitableRole,
    });
    setSending(false);
    if (!answer.ok) {
      setInviteFailure(answer.message);
      return;
    }
    setInviteOpen(false);
    setInviteName("");
    setInviteEmail("");
    await reload();
  };

  // ---------------------------------------------------------------- resend
  /** Which row is in flight, and which one was just re-sent. */
  const [resending, setResending] = useState<string | null>(null);
  const [resent, setResent] = useState<string | null>(null);

  // --------------------------------------------------------------- removal
  const [removal, setRemoval] = useState<Removal | null>(null);
  const [removing, setRemoving] = useState(false);

  /**
   * `round F5`: the resend, and what it says afterwards.
   *
   * The list is re-read on success, because `P0-IDN-062` RE-ISSUES rather than
   * duplicating: the row's invitation is a new one with a new expiry, and a
   * screen still showing the old one would be showing a link that no longer
   * works. A refusal is the door's own sentence, in the place this screen
   * already puts them.
   */
  const resend = async (member: TeamMember): Promise<void> => {
    if (member.invitationId === null || resending !== null) return;
    setResending(member.id);
    setResent(null);
    try {
      const answer = await resendInvitation(member.invitationId);
      if (!answer.ok) {
        setInviteFailure(answer.message);
        return;
      }
      setInviteFailure(null);
      setResent(member.id);
      await reload();
    } finally {
      setResending(null);
    }
  };

  const openRemoval = async (member: TeamMember): Promise<void> => {
    // **The door is read BEFORE the gesture is offered**, which is the whole of
    // 6.7's requirement and the reason this is not a plain confirm dialog.
    setRemoval({ member, verdict: null, refusal: null });
    const answer = await readDeactivation(member.id);
    setRemoval(
      answer.ok
        ? { member, verdict: answer.value, refusal: null }
        : { member, verdict: null, refusal: answer.message },
    );
    // `round F6`, 6.7: **the manager by default**, which is the law's own
    // phrase and the drawing's « (par défaut) ». Set when the dialog opens, so
    // a manager who reads and presses moves the cars to himself without
    // choosing , and a manager who wants somebody else chooses them.
    setHandTo(selfId);
  };

  const confirmRemoval = async (): Promise<void> => {
    if (removal === null || removing) return;
    setRemoving(true);
    // The target rides the removal when there is something to move; the door
    // refuses in words if it is missing, and answers how many moved.
    const answer = await deactivate(
      removal.member.id,
      (removal.verdict?.vehicles.length ?? 0) > 0 ? handTo : undefined,
    );
    setRemoving(false);
    if (!answer.ok) {
      setRemoval({ ...removal, refusal: answer.message });
      return;
    }
    setRemoval(null);
    await reload();
  };

  /**
   * What the door's refusal means, in words, and it is the DOOR's reading.
   *
   * `openFiles` is `null` when the count could not be established , offboarding's
   * own rule that unknown is not zero , and printing `0` beside a refusal would
   * be reporting a number nobody measured.
   */
  const removalReason = (verdict: Deactivation): string =>
    verdict.openFiles === null
      ? t("dealer.team.removeUnknown")
      : `${t("dealer.team.removeBlocked")} ${String(verdict.openFiles)}`;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("dealer.team.title")}</h1>
          {/* The drawing's own sentence (`hakili-dealer.jsx:3278-3281`): it
              says who opened the dealership and who invites whom, which is
              `I16` in words a manager reads on his first afternoon. */}
          {/* `FX-1`, `M1`: the drawing's sentence names who opened the dealership and
              who invites whom , « Hakili a ouvert la concession et vous a nommé
              responsable » , and it is addressed to the responsable. The employee
              reads the catalogue's own neutral line instead; neither is invented
              here, both are the record's. */}
          <p className="mt-1.5 text-sm text-gray-500" data-testid="team-intro">
            {manages ? t("dealer.team.introDrawn") : t("dealer.team.intro")}
          </p>
        </div>
        {invitableRole === null ? null : (
          <span data-testid="team-invite-open">
            {/* `round F5`, to the pair: the drawing's own control , a `+`, one
                line, and « Inviter un employé ». The word matters and is not a
                synonym: `may_invite_roles` allows a manager exactly ONE role,
                and « collaborateur » described a choice this door does not
                offer. */}
            <Button
              className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap"
              onClick={() => {
                setInviteFailure(null);
                setInviteOpen(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t("dealer.team.invite")}
            </Button>
          </span>
        )}
      </div>

      {/**
       * **The four figures the drawing puts above the list**
       * (`hakili-dealer.jsx:3259-3265`), every one of them DERIVED.
       *
       * Two come from the team the door answered; two come from the park, and
       * they are the drawing's own cut of it , what is still being worked, and
       * what has a visit on it. The park is read through the SAME door the
       * inventory and the dashboard read, so the three screens cannot disagree
       * about how many cars are in play.
       *
       * A manager opens this screen to ask « who is doing what ». Two of these
       * four answer « what », and the drawing's own per-person half of the
       * question is the one no column can answer yet , said once under the
       * list rather than four times as a zero.
       */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="team-tuiles">
        <Card>
          <p className={`${METADONNEE} ${TEXTE_SECONDAIRE}`}>{t("dealer.team.tuilePersonnes")}</p>
          <p className={`mt-1 text-2xl font-semibold text-indigo-600 ${CHIFFRE}`} data-testid="team-actifs">
            {String((members ?? []).filter((row) => row.status === "actif").length)}
          </p>
          <p className={`mt-1 ${METADONNEE} ${TEXTE_TERTIAIRE}`}>{t("dealer.team.tuilePersonnesSuite")}</p>
        </Card>
        <Card>
          <p className={`${METADONNEE} ${TEXTE_SECONDAIRE}`}>{t("dealer.team.tuileInvitations")}</p>
          <p className={`mt-1 text-2xl font-semibold ${CHIFFRE}`} data-testid="team-invitations">
            {String((members ?? []).filter((row) => row.status === "invitationEnAttente").length)}
          </p>
          <p className={`mt-1 ${METADONNEE} ${TEXTE_TERTIAIRE}`}>{t("dealer.team.tuileInvitationsSuite")}</p>
        </Card>
        <Card>
          <p className={`${METADONNEE} ${TEXTE_SECONDAIRE}`}>{t("dealer.team.tuileVehicules")}</p>
          <p className={`mt-1 text-2xl font-semibold ${CHIFFRE}`} data-testid="team-vehicules">
            {String(park.filter((car) => weighsOnTheTeam(car.stage)).length)}
          </p>
          <p className={`mt-1 ${METADONNEE} ${TEXTE_TERTIAIRE}`}>{t("dealer.team.tuileVehiculesSuite")}</p>
        </Card>
        <Card>
          <p className={`${METADONNEE} ${TEXTE_SECONDAIRE}`}>{t("dealer.team.tuileVisites")}</p>
          <p className={`mt-1 text-2xl font-semibold ${CHIFFRE}`} data-testid="team-visites">
            {String(park.filter((car) => VISITING.has(car.stage)).length)}
          </p>
          <p className={`mt-1 ${METADONNEE} ${TEXTE_TERTIAIRE}`}>{t("dealer.team.tuileVisitesSuite")}</p>
        </Card>
      </div>

      {/* The drawing's own heading over the list (`hakili-dealer.jsx:3345`).
          Four figures and then a list with nothing between them read as one
          block; the heading is what separates « combien » from « qui ». */}
      <h2 className="text-sm font-semibold" data-testid="team-personnes">
        {t("dealer.team.personnes")}
      </h2>

      <Card>
        {readFailure !== null ? (
          <p role="alert" className="text-sm text-rose-600" data-testid="team-read-failure">
            {readFailure}
          </p>
        ) : members === null ? (
          <p className="text-sm text-gray-500">{t("common.state.chargement")}</p>
        ) : (
          <ul
            // `DEALER-EQUIPE-ROWS`, R's review of 3 November: one grid for the whole list, so the état and charge columns take the width of their CONTENT and stand at one x on every row;
            // the actions column is fixed (12.5 rem, the two actions stacked at every width, R's change 7), and the employee's list has NO actions column at all.
            className={`grid divide-y divide-gray-100 ${
              manages
                ? "grid-cols-[auto_minmax(0,1fr)_auto_auto_12.5rem]"
                : "grid-cols-[auto_minmax(0,1fr)_auto_auto]"
            }`}
          >
            {inDrawnOrder(members).map((member) => {
              // A closed account STAYS in the list, greyed and dated. Deleting
              // the row would erase the trace of who carried what , the console
              // mockup's own sentence, and 6.7's.
              const removed = member.status === "desactive";
              const roleKey = ROLE_LABEL[member.role];
              const statusKey = STATUS_LABEL.get(member.status);
              return (
                <li
                  key={member.id}
                  className="col-span-full grid grid-cols-subgrid"
                  data-testid={`team-member-${member.id}`}
                  data-state={member.status}
                >
                  <div
                    // `round F5`, to the pair: the row grew two dated lines, so
                    // the face is aligned to the TOP of it as the drawing has it
                    // (`hakili-dealer.jsx:3357`). Centred, it floated beside the
                    // second line and stopped reading as this person's.
                    className="col-span-full grid grid-cols-subgrid items-start gap-x-3 py-3"
                  >
                    <Avatar name={member.fullName} size="sm" />
                    <div className="min-w-0">
                      {/* The drawing's row: the name, the role as a CHIP, « vous »
                        where it is you, then the state. Four facts on one line
                        rather than a name over a role, because the question a
                        manager asks of this screen is « who », and « who » is
                        all four at once. */}
                      <div className="flex flex-col items-start gap-1.5">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <p
                            className={`text-sm font-semibold ${removed ? "text-gray-500" : "text-gray-900"}`}
                          >
                            {member.fullName}
                          </p>
                          {member.id === selfId ? <Chip tone="neutre">{t("dealer.team.vous")}</Chip> : null}
                        </div>
                        {/**
                         * `round F5`: the role chip is NEUTRAL, both rungs.
                         *
                         * It was `attention` for the manager , amber , which
                         * 4.6.3 spends on a state that wants looking at. Being
                         * the manager is not a state and wants nothing; the word
                         * in the chip already says which rung this is. The
                         * drawing tints it with the accent, and 4.6.3's tone
                         * vocabulary has four tones and no accent tone , filed as
                         * a candidate rather than answered by inventing a fifth
                         * here.
                         */}
                        <Chip tone="neutre">{roleKey === undefined ? member.role : t(roleKey)}</Chip>
                      </div>
                      {/* The address, which is what a manager copies to write to
                        somebody , and, for an invitation, the sentence that
                        says where the link went. An invitation with no address
                        on the row is a manager guessing whom he invited. */}
                      <p
                        className="mt-1 break-words text-xs text-gray-500"
                        data-testid={`team-email-${member.id}`}
                      >
                        {member.status === "invitationEnAttente"
                          ? `${t("dealer.team.invitationEnvoyee")} ${member.email}`
                          : member.email}
                      </p>
                      {/**
                       * `round F5`: **the dates the drawing puts on every row.**
                       *
                       * `EquipeScreen` writes « Dans l'équipe depuis le 18 juin
                       * 2026 · dernière connexion aujourd'hui à 08:20 ». The door
                       * has always carried the second and now carries the first,
                       * and **neither may be supplied by a screen**: `A50`, a
                       * browser's clock knows when the page was opened, not when
                       * an account was made.
                       *
                       * Each half is shown only where the door sent it. An
                       * account that has never signed in has no last login, and a
                       * row that said « dernière connexion , » with nothing after
                       * it would be the dated-removal defect of `P0-DLW-008`
                       * again, one column further along.
                       */}
                      {member.status === "invitationEnAttente" ||
                      (member.createdAt === null && member.lastLoginAt === null) ? null : (
                        <p
                          className={`mt-0.5 ${METADONNEE} ${META_TEXT}`}
                          data-testid={`team-dates-${member.id}`}
                        >
                          {/* One line each, so the left column does not wrap the two facts into five lines at 960. */}
                          {member.createdAt === null ? null : (
                            <span className="block">{`${t("dealer.team.depuis")} ${whole(formatDate(dayOf(member.createdAt)))}`}</span>
                          )}
                          {member.lastLoginAt === null ? null : (
                            <span className="block">{`${t("dealer.team.derniereConnexion")} ${whole(formatDate(dayOf(member.lastLoginAt)))}`}</span>
                          )}
                        </p>
                      )}
                    </div>
                    {/**
                     * `round F6`: **what this person follows, and what it asks of
                     * them**, from the park this screen already read.
                     *
                     * The drawing puts three counts on the row and « Voir ses
                     * véhicules » beside them (`:3399-3437`). A person who
                     * follows nothing carries no counts at all , three zeroes
                     * would be a row shouting that somebody is idle.
                     */}

                    {/* `DEALER-EQUIPE-ROWS`: the three right-hand slots have ONE width on every row, so the state, the load and the actions stand in columns. « Actif » says nothing and is not drawn; a chip is for a state that is not. */}
                    <div className="max-w-44 text-right" data-testid={`team-colonne-statut-${member.id}`}>
                      {member.status === "actif" ? null : (
                        <Chip tone={STATUS_TONE.get(member.status) ?? "neutre"}>
                          {removed && member.deactivatedOn !== null
                            ? `${t("dealer.team.statusRemoved")} ${formatDate(dayOf(member.deactivatedOn))}`
                            : statusKey === undefined
                              ? member.status
                              : t(statusKey)}
                        </Chip>
                      )}
                    </div>
                    <div className="whitespace-nowrap" data-testid={`team-colonne-charge-${member.id}`}>
                      {removed || member.status === "invitationEnAttente" ? null : (
                        <span data-testid={`team-charge-${member.id}`}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setUnfolded(unfolded === member.id ? null : member.id);
                            }}
                          >
                            {/* `pl` and not a hard-coded plural: « 1 véhicules
                              en cours » is what a template writes, and the pair
                              of 8 September is where it was read. The drawing
                              uses its own `pl` for the same line (`:924`). */}
                            {loadOf(park, member.id).held.length === 0
                              ? t("dealer.team.chargeAucune")
                              : `${pl(loadOf(park, member.id).weighing, t("dealer.team.chargeVehicule"))} ${t("dealer.team.chargeEnCours")}`}
                          </Button>
                        </span>
                      )}
                    </div>
                    {manages ? (
                      <div
                        className="flex flex-col items-end gap-2 whitespace-nowrap"
                        data-testid={`team-colonne-actions-${member.id}`}
                      >
                        {/* No control on a row already closed, and none on one's own:
                      a manager removing themselves would leave a dealership
                      nobody can run, and the api has no second manager to
                      promote. */}
                        {/**
                         * `round F5`, found work of round F: **« Renvoyer », alive.**
                         *
                         * The drawing's own bullet in the invitation panel: *« Tant
                         * qu'elle n'a pas accepté, vous pouvez renvoyer l'invitation
                         * depuis la liste »*. The door has existed since
                         * `P0-IDN-062` and was closed to the one person who needs it
                         * , its layer 2 named three rights and a dealership's manager
                         * holds none of them , so the control could not be built and
                         * the drawing described something no screen could do.
                         *
                         * It appears on exactly the rows waiting on an invitation,
                         * because that is where `invitationId` is not null.
                         */}
                        {/* `FX-1`, `M1`: and the RIGHT, not only the row's shape. The park's first
                        frame of `31-equipe-employe` showed « Renvoyer » offered to the employee,
                        whose door answers `403` , the same defect as the removal control, one row
                        over. A spec already asserted « the whole control is behind the gesture
                        right »; it passed because no fixture row awaited an invitation. */}
                        {member.invitationId === null ? null : (
                          <span data-testid={`team-resend-${member.id}`}>
                            <Button
                              variant="secondary"
                              size="sm"
                              inactive={resending !== null}
                              onClick={() => {
                                void resend(member);
                              }}
                            >
                              {resent === member.id ? t("dealer.team.resent") : t("dealer.team.resend")}
                            </Button>
                          </span>
                        )}
                        {removed || member.id === selfId ? null : (
                          <span data-testid={`team-remove-${member.id}`}>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                void openRemoval(member);
                              }}
                            >
                              {t("dealer.team.remove")}
                            </Button>
                          </span>
                        )}
                      </div>
                    ) : null}
                  </div>

                  {/**
                   * `round F6`: **« Voir ses véhicules », unfolded.**
                   *
                   * The drawing's own answer to *qui travaille sur quoi*
                   * (`:3439-3470`): the cars in this person's name, each with
                   * its stage, opened under their row rather than on a screen
                   * of their own. It is the park this screen already read, so
                   * nothing is fetched to open it.
                   */}
                  {unfolded !== member.id ? null : (
                    <div
                      className="col-span-full border-t border-gray-100 bg-gray-50 px-5 py-3"
                      data-testid={`team-vehicules-${member.id}`}
                    >
                      {loadOf(park, member.id).held.length === 0 ? (
                        <p className={`${METADONNEE} ${TEXTE_TERTIAIRE}`}>{t("dealer.team.chargeRien")}</p>
                      ) : (
                        <>
                          <p className={`mb-1.5 tabular-nums ${METADONNEE} ${TEXTE_TERTIAIRE}`}>
                            {`${pl(loadOf(park, member.id).held.length, t("dealer.team.chargeVehicule"))} ${t("dealer.team.chargeANom")} ${String(loadOf(park, member.id).weighing)} ${t("dealer.team.chargeDontEnCours")}`}
                          </p>
                          <ul className="divide-y divide-gray-100">
                            {loadOf(park, member.id).held.map((car) => (
                              <li key={car.id} className="py-2">
                                <div className="flex items-center gap-3">
                                  <span className="min-w-0 flex-1 truncate text-sm text-gray-800">
                                    {car.title}
                                    <span className={`ml-1 ${METADONNEE} ${TEXTE_TERTIAIRE}`}>
                                      · {car.stockReference}
                                    </span>
                                  </span>
                                  <Chip tone={stageTone(car.stage)}>{stageLabel(car.stage)}</Chip>
                                  {/**
                                   * `FX-2a`, `M3` (`F7`): **the line's own gesture.**
                                   *
                                   * The drawing puts it here in these words ,
                                   * *« Chaque ligne se réattribue une à une, sans
                                   * attendre un départ »* , and the door it calls
                                   * has existed since `round F6`. It renders only
                                   * where `App` passed the door, which it does only
                                   * where the right opens.
                                   */}
                                  {assignVehicle === undefined ? null : (
                                    <Button
                                      variant="secondary"
                                      onClick={() => {
                                        setHandingRefusal(null);
                                        setHanding(handing === car.id ? null : car.id);
                                      }}
                                      data-testid={`team-vehicule-reattribuer-${car.id}`}
                                    >
                                      {t("dealer.team.reassignOne")}
                                    </Button>
                                  )}
                                </div>
                                {assignVehicle === undefined || handing !== car.id ? null : (
                                  <div
                                    className="mt-2 ml-7 flex flex-wrap items-center gap-2"
                                    data-testid={`team-vehicule-passer-a-${car.id}`}
                                  >
                                    <span className={`${METADONNEE} ${TEXTE_TERTIAIRE}`}>
                                      {t("dealer.team.reassignTo")}
                                    </span>
                                    {/**
                                     * `candidates` and not a second filter: the
                                     * removal dialog already asks who may take a
                                     * car , active, and not the person holding it ,
                                     * and two ideas of that rule would disagree the
                                     * first time one changed. `labelFor` says WHICH
                                     * person, which is `F7`'s second half.
                                     */}
                                    {candidates(members, member.id, selfId).map((person) => (
                                      <Button
                                        key={person.id}
                                        variant="secondary"
                                        onClick={() => {
                                          void (async () => {
                                            const answer = await assignVehicle(car.id, person.id);
                                            if (!answer.ok) {
                                              setHandingRefusal(answer.message);
                                              return;
                                            }
                                            setHanding(null);
                                            setHandingRefusal(null);
                                            // The PARK re-read from its own door rather
                                            // than patched here: the list under every row
                                            // is derived from it, and a screen that edited
                                            // its copy would show the car in two hands
                                            // until the next visit.
                                            const again = await listVehicles();
                                            if (again.ok) setPark(again.value.vehicles);
                                          })();
                                        }}
                                        data-testid={`team-vehicule-passer-a-${car.id}-${person.id}`}
                                      >
                                        {labelFor(person, selfId)}
                                      </Button>
                                    ))}
                                    {handingRefusal === null ? null : (
                                      <p
                                        role="alert"
                                        className="text-sm text-rose-600"
                                        data-testid={`team-vehicule-passer-a-${car.id}-refus`}
                                      >
                                        {handingRefusal}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {/**
         * **`round F6`: the sentence that stood here is DELETED, not amended.**
         *
         * It said the load and « Voir ses véhicules » would appear *quand un
         * véhicule portera le nom de qui le suit*. A car carries that name now,
         * the load is on every row and the cars unfold under it, so the
         * sentence had become false , and `R81` as the founder amended it wants
         * a dash where something is missing, never a paragraph about something
         * that has arrived.
         */}
      </Card>

      {/**
       * `round F5`, to the pair: **the invitation is a PANEL, not a dialog.**
       *
       * The drawing slides it in from the right, *« de la même famille que
       * celui de l'ajout d'un véhicule »* (`hakili-dealer.jsx:3003`), and that
       * family is `SidePanel` , the same component « Ajouter un véhicule »
       * already uses. A centred dialog covered the list it is about; the panel
       * leaves the team where it was and dims it, which is the difference
       * `SidePanel`'s own note spells out.
       *
       * Three things the dialog did not carry come with it: the ROLE, said out
       * loud rather than chosen silently from `may_invite_roles`; what happens
       * next, in four sentences; and a refusal that names which field is
       * wrong.
       */}
      <SidePanel
        open={inviteOpen}
        title={t("dealer.team.invite")}
        {...(organisation === null || organisation === "" ? {} : { subtitle: organisation })}
        closeLabel={t("common.action.fermer")}
        onClose={() => {
          setInviteOpen(false);
        }}
      >
        <div className="mt-5 flex flex-1 flex-col gap-4">
          <Field label={t("dealer.team.inviteName")} htmlFor="team-invite-name">
            <input
              id="team-invite-name"
              value={inviteName}
              placeholder={t("dealer.team.inviteNamePlaceholder")}
              onChange={(event) => {
                setInviteName(event.target.value);
              }}
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm placeholder:text-gray-300"
              data-testid="team-invite-name"
            />
          </Field>
          <Field label={t("dealer.team.inviteEmail")} htmlFor="team-invite-email">
            <input
              id="team-invite-email"
              type="email"
              value={inviteEmail}
              placeholder={t("dealer.team.inviteEmailPlaceholder")}
              onChange={(event) => {
                setInviteEmail(event.target.value);
              }}
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm placeholder:text-gray-300"
              data-testid="team-invite-email"
            />
            <p className={`mt-1 ${METADONNEE} ${TEXTE_TERTIAIRE}`}>{t("dealer.team.inviteEmailHint")}</p>
          </Field>

          {/* The role, SHOWN and not chosen. `may_invite_roles` allows exactly
              one, so a select would be a control with a single option; what a
              manager needs instead is to know what he is handing over, which
              is the sentence under the word. */}
          <div>
            <p className={`mb-1.5 ${METADONNEE} font-medium ${TEXTE_SECONDAIRE}`}>
              {t("dealer.team.inviteRole")}
            </p>
            <div
              className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3"
              data-testid="team-invite-role"
            >
              <p className="text-sm font-semibold text-indigo-700">
                {invitableRole === null ? "" : (roleWord(ROLE_CHIP, invitableRole) ?? invitableRole)}
              </p>
              <p className="mt-1 text-xs text-gray-600">{t("dealer.team.inviteRoleSentence")}</p>
            </div>
          </div>

          {/* What happens next, in the drawing's own four sentences. The last
              two are the ones a manager acts on: the invitation can be sent
              again from the list, and the link itself is never shown here. */}
          <div className="rounded-xl bg-gray-50 px-4 py-3" data-testid="team-invite-next">
            <p className={`${METADONNEE} font-medium ${TEXTE_SECONDAIRE}`}>{t("dealer.team.inviteNext")}</p>
            <ul className="mt-2 flex flex-col gap-2">
              {[
                "dealer.team.inviteNext1",
                "dealer.team.inviteNext2",
                "dealer.team.inviteNext3",
                "dealer.team.inviteNext4",
              ].map((key) => (
                <li key={key} className="flex items-start gap-2 text-xs text-gray-500">
                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-300" aria-hidden="true" />
                  {t(key)}
                </li>
              ))}
            </ul>
          </div>

          {inviteFailure === null ? null : (
            <p role="alert" className="text-sm text-rose-600" data-testid="team-invite-failure">
              {inviteFailure}
            </p>
          )}

          <div className="mt-auto flex items-center justify-between gap-3 pt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setInviteOpen(false);
              }}
            >
              {t("common.action.annuler")}
            </Button>
            {/* The reason BESIDE the closed control, in the drawing's own place
                (`hakili-dealer.jsx:3097-3098`) and only once something has been
                typed. `GuardedButton` carries it for a reader who cannot see;
                this is the half a manager reads without hovering anything. */}
            {inviteRefusal === null || !started ? null : (
              <span
                className={`ml-auto text-right ${METADONNEE} text-amber-700`}
                data-testid="team-invite-raison"
              >
                {inviteRefusal}
              </span>
            )}
            <span data-testid="team-invite-send">
              {inviteRefusal === null ? (
                <Button
                  inactive={sending}
                  onClick={() => {
                    void send();
                  }}
                >
                  {t("dealer.team.inviteSend")}
                </Button>
              ) : (
                // `I19` through `GuardedButton`: the control is closed and the
                // reason is on it. The drawing greys it the same way and puts
                // the same words under the form.
                <GuardedButton disabled reason={inviteRefusal}>
                  {t("dealer.team.inviteSend")}
                </GuardedButton>
              )}
            </span>
          </div>
        </div>
      </SidePanel>

      {removal === null ? null : (
        <div data-testid="team-remove-dialog">
          <Modal
            open
            title={t("dealer.team.removeTitle")}
            closeLabel={t("common.action.fermer")}
            onClose={() => {
              setRemoval(null);
            }}
            actions={
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setRemoval(null);
                  }}
                >
                  {t("common.action.annuler")}
                </Button>
                {removal.verdict === null ? null : (
                  <span data-testid="team-remove-confirm">
                    {removal.verdict.allowed ? (
                      <Button
                        onClick={() => {
                          void confirmRemoval();
                        }}
                      >
                        {t("dealer.team.removeConfirm")}
                      </Button>
                    ) : (
                      // `I19` through `GuardedButton`: a blocked control that
                      // does not say why is the most expensive pixel in a
                      // workflow product, and the type here refuses to compile
                      // without the reason.
                      <GuardedButton disabled reason={removalReason(removal.verdict)}>
                        {t("dealer.team.removeConfirm")}
                      </GuardedButton>
                    )}
                  </span>
                )}
              </>
            }
          >
            {/* The drawing's own head: the face, the name, and the ROLE
                (`hakili-dealer.jsx:3139-3145`). A manager about to close an
                account is looking for the person, and « Employé » beside the
                name is what tells him he has the right one. */}
            <div className="flex items-center gap-3">
              <Avatar name={removal.member.fullName} size="sm" />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-900">{removal.member.fullName}</p>
                <p className={`truncate ${METADONNEE} ${TEXTE_TERTIAIRE}`}>
                  {roleWord(ROLE_LABEL, removal.member.role) ?? removal.member.role}
                  {removal.member.email === "" ? "" : ` · ${removal.member.email}`}
                </p>
              </div>
            </div>

            {/* `FX-1`, `M1` (`F6`): a REFUSED read is an answer, and an answer is not a
                wait. The loading line was drawn on `verdict === null` alone, so a door
                that refused left « Chargement… » standing beside its own sentence , the
                founder's second reading of the sitting. The refusal is rendered below. */}
            {removal.verdict === null ? (
              // A refused read is an ANSWER: the wait yields to it, and the door's own
              // sentence is rendered below. Drawing both was the sitting's second reading.
              removal.refusal === null ? (
                <p className="mt-3 text-sm text-gray-500">{t("common.state.chargement")}</p>
              ) : null
            ) : removal.verdict.allowed ? (
              // The sentences 6.7 requires a manager to have read before they
              // press , and, since `round F6`, WHAT MOVES and where. A fragment
              // because there are two blocks now: what is at their name today,
              // and what the gesture does.
              <>
                {/**
                 * `round F6`, 6.7: **what is at their name today, and where it
                 * goes.**
                 *
                 * The drawing's own block (`:3153-3200`): the cars listed, then
                 * « À qui cela revient » with the manager marked as the default.
                 * A person who holds nothing gets neither , the drawing says so
                 * too: *« Rien en cours à son nom. Le geste garde le même nom :
                 * il n'y a simplement rien à transmettre. »*
                 */}
                {removal.verdict.vehicles.length === 0 ? null : (
                  <div className="mt-3 rounded-xl border border-gray-200 px-4 py-3">
                    <p className={`${METADONNEE} font-medium ${TEXTE_SECONDAIRE}`}>
                      {t("dealer.team.removeHolds")}
                    </p>
                    <ul className="mt-2 divide-y divide-gray-50" data-testid="team-remove-vehicules">
                      {removal.verdict.vehicles.map((car) => (
                        <li key={car.id} className="flex items-center gap-3 py-1.5">
                          <span className="min-w-0 flex-1 truncate text-xs text-gray-700">
                            {car.title}
                            <span className={`ml-1 ${TEXTE_TERTIAIRE}`}>· {car.stockReference}</span>
                          </span>
                          <Chip tone={stageTone(car.stage)}>{stageLabel(car.stage)}</Chip>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3">
                      <Field label={t("dealer.team.removeTarget")} htmlFor="team-remove-target">
                        <span data-testid="team-remove-target">
                          <Select
                            id="team-remove-target"
                            value={handTo}
                            onChange={(value) => {
                              setHandTo(value);
                            }}
                            options={candidates(members ?? [], removal.member.id, selfId).map((person) => ({
                              value: person.id,
                              // `FX-2a`, `M3` (`F7`): the label identifies the person.
                              label: labelFor(person, selfId),
                            }))}
                          />
                        </span>
                      </Field>
                    </div>
                  </div>
                )}

                <div className="mt-3 rounded-xl bg-gray-50 px-4 py-3">
                  <p className={`${METADONNEE} font-medium ${TEXTE_SECONDAIRE}`}>
                    {t("dealer.team.removeWhat")}
                  </p>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-600">
                    {/* `round F6`: the first bullet is now a FACT about this
                      person , how many cars move and to whom , and the old
                      sentence stands where it is still true, for somebody who
                      holds nothing. */}
                    <li>
                      {/* The number, the noun and the VERB all agree: « 1
                          véhicules passent » is what a template with a
                          hard-coded plural writes, and the pair of 8 September
                          read it on this very line. */}
                      {removal.verdict.vehicles.length === 0
                        ? t("dealer.team.removeHoldsNothing")
                        : `${pl(removal.verdict.vehicles.length, t("dealer.team.chargeVehicule"))} ${
                            removal.verdict.vehicles.length >= 2
                              ? t("dealer.team.removeMovesPlural")
                              : t("dealer.team.removeMoves")
                          } ${nameOf(members ?? [], handTo)}${t("dealer.team.removeMovesTrace")}`}
                    </li>
                    <li>{t("dealer.team.removeNeverDeleted")}</li>
                    <li>{t("dealer.team.removeRowStays")}</li>
                    {/* `round F5`, to the pair: two the door does and the dialog
                      never said. Both were read out of the api before they
                      were written here , a bullet nobody verified is a promise
                      a screen makes on a door's behalf. */}
                    <li>{t("dealer.team.removeSessions")}</li>
                    <li>{t("dealer.team.removeSilent")}</li>
                  </ul>
                </div>
              </>
            ) : (
              <div className="mt-3">
                {/* The reassignment target of the day the door exists , 6.7's
                    own default. It is inert today and says so: offering a
                    control that silently did nothing would be worse than not
                    offering one, and redesigning this dialog later is what the
                    law asks us to avoid. */}
                <Field label={t("dealer.team.removeTarget")} htmlFor="team-remove-target">
                  {/* Wrapped rather than marked: `@hakili/ui`'s primitives take
                      no `data-testid`, deliberately, and widening the kit for one
                      screen is the direction 4.6.2 refuses. */}
                  <span data-testid="team-remove-target">
                    <Select
                      id="team-remove-target"
                      value={selfId}
                      options={[{ value: selfId, label: t("dealer.team.removeTargetManager") }]}
                    />
                  </span>
                </Field>
                <p className="mt-2 text-sm text-amber-700" data-testid="team-remove-reason">
                  {removalReason(removal.verdict)}
                </p>
                <p className="mt-2 text-xs text-gray-500">{t("dealer.team.removeOwed")}</p>
              </div>
            )}

            {removal.refusal === null ? null : (
              <p role="alert" className="mt-3 text-sm text-rose-600" data-testid="team-remove-failure">
                {removal.refusal}
              </p>
            )}
          </Modal>
        </div>
      )}
    </div>
  );
}
